import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, MessageCircle, Menu, X, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { COMPANY_INFO, NAV_LINKS } from '../mock';
import { LOGO_DARK_BG, LOGO_LIGHT_BG } from '../config/branding';

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest('header')) {
        setMobileMenuOpen(false);
      }
    };
    
    if (mobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <motion.header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500
        ${scrolled 
          ? 'h-16 lg:h-18 bg-white/80 backdrop-blur-xl border-b border-slate-200/50 shadow-sm' 
          : 'h-20 lg:h-24 bg-transparent'
        }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: [0.25, 0.4, 0.25, 1] }}
    >
      {/* Desktop Layout */}
      <div className="hidden md:block max-w-7xl mx-auto px-6 lg:px-8 h-full">
        <div className="flex justify-between items-center h-full">
          {/* Logo */}
          <motion.div 
            className="flex items-center"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <a href="/" className="flex items-center group">
              <img 
                src={scrolled ? LOGO_LIGHT_BG : LOGO_DARK_BG}
                alt="Dein Gutachten Direkt Logo" 
                className={`w-auto transition-all duration-300 ${scrolled ? 'h-10' : 'h-12'}`}
              />
            </a>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="flex items-center gap-1">
            {NAV_LINKS.map((link, idx) => (
              <a
                key={link.href}
                href={link.href}
                className={`px-4 py-2 text-[15px] font-medium rounded-lg transition-all duration-300 relative group
                  ${scrolled ? 'text-slate-600 hover:text-[#0f172a] hover:bg-slate-100' : 'text-white/80 hover:text-white hover:bg-white/10'}`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="flex items-center gap-3">
            <a href="/portal/login" className={`text-sm font-medium transition-colors ${scrolled ? 'text-slate-600 hover:text-[#22c55e]' : 'text-white/70 hover:text-white'}`}>
              Partner Login
            </a>
            <a href={`tel:${COMPANY_INFO.phone}`}>
              <Button className="bg-[#22c55e] hover:bg-[#16a34a] text-white font-semibold text-[15px] h-11 px-6 rounded-full shadow-lg shadow-[#22c55e]/25 hover:shadow-xl hover:shadow-[#22c55e]/30 hover:-translate-y-0.5 transition-all duration-300 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Kostenlos anrufen
                <ArrowRight className="w-4 h-4 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className={`md:hidden flex items-center justify-between h-full px-4 ${scrolled ? '' : ''}`}>
        {/* Logo */}
        <a href="/" className="flex items-center">
          <img 
            src={scrolled ? LOGO_LIGHT_BG : LOGO_DARK_BG}
            alt="Dein Gutachten Direkt Logo" 
            className={`w-auto transition-all ${scrolled ? 'h-8' : 'h-9'}`}
          />
        </a>

        {/* Mobile Menu Toggle */}
        <button
          className={`p-2.5 rounded-xl transition-all duration-300 ${scrolled ? 'text-slate-600 hover:bg-slate-100' : 'text-white hover:bg-white/10'}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            className="absolute left-0 right-0 top-full z-50 md:hidden bg-white border-t border-slate-100 shadow-2xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            <nav className="px-4 py-4 space-y-1">
              {NAV_LINKS.map((link, idx) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  className="block px-4 py-3.5 text-base font-medium text-slate-700 hover:bg-slate-50 hover:text-[#22c55e] rounded-xl transition-all"
                  onClick={() => setMobileMenuOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  {link.label}
                </motion.a>
              ))}
              <div className="border-t border-slate-100 my-2" />
              <motion.a
                href="/portal/login"
                className="block px-4 py-3.5 text-base font-medium text-[#22c55e] hover:bg-[#22c55e]/5 rounded-xl transition-all"
                onClick={() => setMobileMenuOpen(false)}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                Partner Login →
              </motion.a>
              
              {/* Mobile CTA */}
              <div className="pt-4 space-y-3">
                <a href={`tel:${COMPANY_INFO.phone}`} className="block">
                  <Button className="w-full bg-[#22c55e] hover:bg-[#16a34a] text-white font-semibold py-4 rounded-xl">
                    <Phone className="w-5 h-5 mr-2" />
                    Jetzt anrufen
                  </Button>
                </a>
                <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer" className="block">
                  <Button variant="outline" className="w-full border-2 border-slate-200 text-slate-700 font-semibold py-4 rounded-xl hover:border-[#22c55e] hover:text-[#22c55e]">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};